'use client';

import Image, { ImageLoaderProps, ImageProps } from "next/image";
import { useEffect, useState, memo, forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { appConfig } from '../../config';

interface Props extends ImageProps {
  forceUpdate?: boolean;
  isUser?: boolean;
  thumbnail?: string; 
  isLarge?: boolean;
  eager?: boolean;
  customDimensions?: Partial<{
    height: number;
    width: number;
    fit: 'cover' | 'contain';
  }>;
}

const getResizedImageUrl = (src: string, options: Props['customDimensions'] = {}) => {
  if (!src) return '';
  const base = new URL(`https://erem-media-service.azurewebsites.net/api/ResizeImage`);
  base.searchParams.set('image', src);
  const {height, width, fit} = options;
  if (height) base.searchParams.set('height', String(height));
  if (width) base.searchParams.set('width', String(width));
  if (fit) base.searchParams.set('fit', fit);
  return base.href;
}


const extensions = [''];

const filterExtension = (src: string) => {
  let result = src;
  extensions.forEach(e => result.replace(e, ''));
  return result;
}

const  FallBackImage = forwardRef(({ 
  src, 
  isLarge,
  alt, 
  isUser,
  forceUpdate, 
  thumbnail, 
  customDimensions = {width: 800, height: 450, fit: 'cover'},
  ...rest }: Props, ref: any) => {
    const placeholderImageSrc = isUser ? '/assets/images/user-default-image2.png' : '/assets/images/default-image2.png';
    const placeholderImage = {src: placeholderImageSrc, thumbnail: placeholderImageSrc};
    const isPlaceholder = !src;
  let srcs = src ?  extensions.map(e => ({src: `${filterExtension(src as string)}${e}`, thumbnail: thumbnail ? `${filterExtension(thumbnail as string)}${e}` : placeholderImage.thumbnail})).concat(placeholderImage) : [placeholderImage];
  const [{ selected, index, loaded }, setState] = useState<{ selected: {src: string; thumbnail: string}; index: number; loaded: boolean; }>(({ loaded: false, selected: srcs[0], index: 0 }));
  useEffect(() => {
    if (!forceUpdate) {
      if (!src) {
        setState(prevState => ({...prevState,selected: srcs[0], loaded: true}));
      }
      return;
    }
    srcs = src ?  extensions.map(e => ({src: src ? `${filterExtension(src as string)}${e}` : placeholderImage.src, thumbnail: thumbnail ? `${filterExtension(thumbnail as string)}${e}` : placeholderImage.thumbnail})).concat(placeholderImage) : [placeholderImage];
    setState({ loaded: true, selected: srcs[0], index: 0 });
  }, [src]);

  const eager = rest.eager || rest.loading === 'eager' || rest.priority;
  //@ts-ignore
  ['priority', 'loading', 'sizes', 'fill', 'eager'].forEach(x => delete rest[x]);

  const props: any = {
    ...rest,
    alt,
  }

  return (
    <div className={twMerge('w-full relative', isLarge ? 'aspect-[16/9]' : 'aspect-[4/3]', props.className,)}>
      
     <picture>
   {/* ===================== */}
  {/* MOBILE (max 767px)    */}
  {/* ===================== */}

  {!isPlaceholder && !isUser && (
    <source
      media="(max-width: 767px)"
      type="image/webp"
      srcSet={`${getResizedImageUrl(`${selected.src}.webp`, customDimensions)}`}
    />
  )}

  <source
    media="(max-width: 767px)"
    type="image/png"
    srcSet={
      isPlaceholder
        ? selected.src
        : getResizedImageUrl(selected.src, customDimensions)
    }
  />

  {/* ===================== */}
  {/* TABLET (max 1023px)   */}
  {/* ===================== */}

  {!isPlaceholder && !isUser && (
    <source
      media="(max-width: 1023px)"
      type="image/webp"
      srcSet={`${getResizedImageUrl(`${selected.src}.webp`, customDimensions)}`}
    />
  )}

  <source
    media="(max-width: 1023px)"
    type="image/png"
    srcSet={
      isPlaceholder
        ? selected.src
        : getResizedImageUrl(selected.src, customDimensions)
    }
  />

  {/* ===================== */}
  {/* DESKTOP (default)     */}
  {/* ===================== */}

  {!isPlaceholder && !isUser && (
    <source
      type="image/webp"
      srcSet={`${selected.src}.webp`}
    />
  )}

  <source
    type="image/png"
    srcSet={selected.src}
  />

  {/* ===================== */}
  {/* FINAL FALLBACK        */}
  {/* ===================== */}

  <img
    src={selected.src}
    alt={alt}
    {...props}
    className={twMerge('w-full', isLarge ? 'aspect-[16/9]' : 'aspect-[4/3]', props.className,)}
    fetchPriority={eager ? 'high' : 'low'}
    loading={eager ? 'eager' : 'lazy'}
  />
    </picture>
        </div>
  )
});


export default FallBackImage;